import { queryOptions, useQuery } from "@tanstack/react-query";
import { assetsApiRequest } from "~api/api-request";
import { ApiResponse } from "~api/api-response.model";
import { useRequiredParams } from "~utils/url";
import { DateTime, Duration } from "luxon";
import { Graphics, GraphicsAttachment } from "./graphics.model";
import { QueryOptions } from "../utils/consts";
import { PointsHistoryParams } from "../point/point-history/point-history.queries";
import { Point, PuckValue } from "../point/point.model";
import { normalizeGraphicValues } from "./history/history-replay.utils";
import { Level } from "../level/level.model";

export const HISTORY_REPLAY_PREVIEW_KEY = "history-replay-preview";

type GraphicsParams = {
  companyId: number;
  graphicsId: number;
};

type GraphicPointGroupInfo = {
  path: string;
  companyId: number;
  graphics: Graphics;
};

export const graphicsQueries = {
  one: (params: GraphicsParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: ["devices-graphics", params],
      queryFn: ({ signal }) => fetchGraphics(params, signal),
      ...options,
    }),
  attachments: (params: GraphicsParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: ["graphics-attachments", params],
      queryFn: ({ signal }) => fetchGraphicsAttachments(params, signal),
      ...options,
    }),
  history: (params: PointsHistoryParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: ["point-history", params],
      queryFn: ({ signal }) => fetchGraphicsHistory(params, signal),
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      ...options,
    }),
  historyPreview: (params: PointsHistoryParams, options?: QueryOptions) =>
    queryOptions({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: [
        HISTORY_REPLAY_PREVIEW_KEY,
        { pointIds: params.pointIds, from: params.from, to: params.to },
      ],
      queryFn: ({ signal }) => fetchGraphicsHistoryPreview(params, signal),
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      ...options,
    }),
  pointGroupInfo: (params: GraphicPointGroupInfo, options?: QueryOptions) =>
    queryOptions({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: [
        "graphics-point-group",
        params.companyId,
        params.path,
        params.graphics?.id,
        params.graphics?.name,
      ],
      queryFn: ({ signal }) => fetchGraphicsPointGroupInfo(params, signal),
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      ...options,
    }),
};

export function useGraphicsQuery(graphicsId: number, options?: QueryOptions) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(graphicsQueries.one({ companyId, graphicsId }, options));
}

export function useGraphicsHistoryQuery(
  { from, to, pointIds, onSuccess }: Omit<PointsHistoryParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(
    graphicsQueries.history(
      { pointIds, companyId, from, to, onSuccess },
      options,
    ),
  );
}

/**
 * A query to fetch default name and description for a point group
 * when creating a group from the graphics view
 * Default name is constructed as follows: Building name - SCADA or Device name - Picture name
 * Default description is consturect as follow: Picture name - Graphic parent 1 - Graphic parent 2
 * all the way up to the image index level
 */
export function useGraphicsPointGroupInfoQuery(
  { graphics, path }: Omit<GraphicPointGroupInfo, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(
    graphicsQueries.pointGroupInfo({ companyId, graphics, path }, options),
  );
}

export function useGraphicsHistoryPreviewQuery(
  { from, to, pointIds, onSuccess }: Omit<PointsHistoryParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(
    graphicsQueries.historyPreview(
      { pointIds, companyId, from, to, onSuccess },
      options,
    ),
  );
}

export function useGraphicsAttachmentsQuery(
  graphicsId: number,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(
    graphicsQueries.attachments({ companyId, graphicsId }, options),
  );
}

async function fetchGraphics(params: GraphicsParams, signal?: AbortSignal) {
  return assetsApiRequest
    .get<
      ApiResponse<Graphics>
    >(`analytics/companies/${params.companyId}/graphics/${params.graphicsId}`, { signal })
    .then((resp) => new Graphics(resp.data.data));
}

function getPointGroupDescription(
  items: Graphics["items"],
  path: string,
  ancestors: string[] = [],
): string | undefined {
  const itemPath = path.toLowerCase();

  for (const item of items) {
    const currentPath = [item.name ?? item.path, ...ancestors];

    if (item.path?.toLowerCase() === itemPath) {
      return currentPath.join(" - ");
    }

    const found = getPointGroupDescription(item.items, path, currentPath);

    if (found) {
      return found;
    }
  }
  return undefined;
}

async function fetchGraphicsPointGroupInfo(
  params: GraphicPointGroupInfo,
  signal?: AbortSignal,
) {
  const level = params.graphics.level;
  const device = params.graphics.device;

  const description = getPointGroupDescription(
    params.graphics.items,
    params.path,
  );

  if (!level && !device) {
    return undefined;
  }

  const graphicName = level ? params.graphics.name : device!.name;
  const nameSuffix = `${graphicName} - ${description?.split(" - ")[0]}`;

  if (level && level.isBuilding) {
    return `${level.name} - ${nameSuffix}`;
  }

  if (device && device.building) {
    return `${device.building} - ${nameSuffix}`;
  }

  const levelId = level ? level.id : device!.levelId;

  const levelWithParents = await assetsApiRequest
    .get<
      ApiResponse<Level>
    >(`analytics/companies/${params.companyId}/levels/${levelId}`, { params: { includeParent: true }, signal })
    .then((resp) => resp.data.data);

  let parentBuildingName = levelWithParents.name;
  let currentLevel = levelWithParents.parent;

  while (currentLevel) {
    if (currentLevel.isBuilding) {
      parentBuildingName = currentLevel.name;
      break;
    }
    currentLevel = currentLevel.parent;
  }

  return `${parentBuildingName} - ${nameSuffix}`;
}

export async function fetchGraphicsAttachments(
  params: GraphicsParams,
  signal?: AbortSignal,
) {
  return assetsApiRequest
    .get<
      ApiResponse<GraphicsAttachment[]>
    >(`analytics/companies/${params.companyId}/graphics/${params.graphicsId}/attachments`, { signal })
    .then((resp) =>
      resp.data.data.map((attachment) => new GraphicsAttachment(attachment)),
    );
}

export type GraphicValue = Omit<PuckValue, "alarmGroup"> & {
  timestampJS: DateTime;
};
export type GraphicHistoryItem = Record<string, GraphicValue[]>;
// ISO 8601 duration per point data type, null for raw data,
// undefined when there are no points of that type
export type HistoryResolution = {
  analog?: string | null;
  digital?: string | null;
};
type GraphicsHistoryResponse = {
  point: Point;
  resolution?: string | null;
  values: GraphicValue[];
}[];

async function fetchGraphicsHistoryPreview(
  params: PointsHistoryParams,
  signal?: AbortSignal,
): Promise<GraphicHistoryItem> {
  const { history } = await fetchGraphicsHistory(params, signal);
  Object.keys(history).forEach((a) => {
    // Normalize values to be between 0 and 1
    history[a] = normalizeGraphicValues(
      // Remove data points that share the same value as the point before and after them
      // This is done to improve performance of the preview chart
      history[a].filter((d, i) => {
        if (i === 0 || i === history[a].length - 1) {
          return true;
        }

        if (
          d.value === history[a][i - 1].value &&
          d.value === history[a][i + 1].value
        ) {
          return false;
        }
        return true;
      }),
    );
  });
  return history;
}

function getCoarserResolution(
  a: string | null | undefined,
  b: string | null,
): string | null {
  if (a === undefined) {
    return b;
  }
  const toMillis = (r: string | null) =>
    r ? Duration.fromISO(r).toMillis() : 0;
  return toMillis(b) > toMillis(a) ? b : a;
}

async function fetchGraphicsHistory(
  { pointIds, companyId, from, to, onSuccess }: PointsHistoryParams,
  signal?: AbortSignal,
): Promise<{ history: GraphicHistoryItem; resolution: HistoryResolution }> {
  const history: GraphicHistoryItem = {};
  const resolution: HistoryResolution = {};

  if (pointIds.length === 0 || !from || !to) {
    return { history, resolution };
  }

  await assetsApiRequest
    .get<ApiResponse<GraphicsHistoryResponse>>(
      `analytics/companies/${companyId}/points/history`,
      {
        signal,
        params: {
          pointIds: pointIds.join(","),
          from: from.toJSDate().toISOString(),
          to: to.toJSDate().toISOString(),
        },
      },
    )
    .then((resp) => {
      resp.data.data.forEach((p) => {
        const dataType = p.values[0]?.dataType;
        if (
          p.resolution !== undefined &&
          (dataType === "analog" || dataType === "digital")
        ) {
          resolution[dataType] = getCoarserResolution(
            resolution[dataType],
            p.resolution,
          );
        }

        p.values.forEach((value) => {
          // Add normal points to our 'history' map
          const pointName = p.point.name;
          const pointValue = historyValueToGraphicValue(
            p.point.name,
            p.point,
            value,
          );
          addValuesToHistory(pointName, pointValue, history);

          // Add limit values to our 'history' map
          value.extension?.limits?.forEach((limit) => {
            const limitName = `${p.point.name}:${limit.index}`;
            const limitValue = historyValueToGraphicValue(limitName, p.point, {
              ...value,
              value: limit.value,
              lockState: limit.lockState,
            });
            addValuesToHistory(limitName, limitValue, history);
          });

          // Add control values to our 'history' map
          Object.entries(value.extension?.control ?? {}).forEach(
            ([key, val]) => {
              const controlName = `${p.point.name}#CONTROL#${key}`;
              const controlValue = historyValueToGraphicValue(
                controlName,
                p.point,
                {
                  ...value,
                  value: +val,
                },
              );
              addValuesToHistory(controlName, controlValue, history);
            },
          );
        });
      });
    });
  onSuccess?.(history);
  return { history, resolution };
}

function addValuesToHistory(
  key: string,
  value: GraphicValue,
  history: GraphicHistoryItem,
) {
  if (Object.hasOwn(history, key)) {
    history[key].push(value);
  } else {
    history[key] = [value];
  }
}
function historyValueToGraphicValue(
  pointName: string,
  point: Point,
  value: GraphicValue,
): GraphicValue {
  return {
    value: value.value,
    alarmState: value.alarmState,
    isAlarmActive: value.isAlarmActive,
    errorState: value.errorState,
    lockState: value.lockState,
    timestamp: value.timestamp,
    timestampJS: DateTime.fromISO(value.timestamp),
    pointName,
    deviceId: point.device.externalId,
    unit: String(point.unit),
    decimals: point.decimals,
    label:
      (point?.stateTexts || []).find(
        (stateText) => stateText.value === value.value,
      )?.label ?? null,
    deviceState: value.deviceState,
    dataType: value.dataType,
  };
}
